// Explore's list: every trace the search reaches, newest first, and above
// them, marked, the traces the analysis names, in the order it names them.
// A row is a trace: when it last did something, its title (its newest
// prompt's summary) and who ran it, its agent and model, how long it ran, what
// it cost, and what review found. Any person or agent filters for or out from
// its row; a row opens its trace.
import { memo } from "react";
import { agentName, durationText } from "../../lib/exploreAnalysis.js";
import { costText } from "../../lib/traceCosts.js";

const SEV = ["critical", "high", "medium", "low"];
const SEV_COLOR = { critical: "var(--severity-critical)", high: "var(--severity-high)", medium: "var(--severity-medium)", low: "var(--severity-low)" };
const NUM = new Intl.NumberFormat("en-US");
// The columns follow the list's own width (it is their container): tighter
// below 980px, as beside the analysis, then Duration and Findings give way
// below 740px and Agent below 480px, so the trace keeps its room.
const COLS = "grid-cols-[82px_minmax(0,1fr)_196px_86px_104px_156px] @max-[980px]:grid-cols-[74px_minmax(0,1fr)_128px_64px_80px_108px] @max-[740px]:grid-cols-[74px_minmax(0,1fr)_128px_80px] @max-[480px]:grid-cols-[74px_minmax(0,1fr)_80px]";
const SHOWN_FROM_740 = "@max-[740px]:hidden";
const SHOWN_FROM_480 = "@max-[480px]:hidden";

// "9:04 pm" for today, else the weekday within the week, else the date.
export function whenText(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const today = new Date(now);
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
  if (now - ms < 6 * 86_400_000) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// The worst severity with its count, then the rest: "1 high", "2 medium, 1 low".
function findingsText(bySev = {}) {
  const have = SEV.filter((s) => bySev[s]);
  if (!have.length) return null;
  return { sev: have[0], first: `${bySev[have[0]]} ${have[0]}`, rest: have.slice(1).map((s) => `${bySev[s]} ${s}`).join(", ") };
}

const quote = (v) => `"${String(v).replace(/"/g, "")}"`;

// Filter for or out of a value, beside it on its row's hover.
function FilterButtons({ name, clause, onFilter }) {
  return (
    <span className="hidden shrink-0 gap-[3px] group-hover/row:inline-flex group-focus-within/row:inline-flex">
      {[["Filter for", clause, "M5 1.5v7M1.5 5h7"], ["Filter out", `-${clause}`, "M1.5 5h7"]].map(([label, c, d]) => (
        <button
          key={label}
          type="button"
          title={`${label} ${name}`}
          aria-label={`${label} ${name}`}
          onClick={(e) => { e.stopPropagation(); onFilter(c); }}
          className="grid h-[18px] w-[18px] place-items-center rounded-[4px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
        >
          <svg viewBox="0 0 10 10" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true"><path d={d} /></svg>
        </button>
      ))}
    </span>
  );
}

const Row = memo(function Row({ row, title, cost, pinned, last, lit, onOpen, onFilter }) {
  const f = findingsText(row.bySev);
  const agent = agentName(row.agent);
  const price = costText(cost);
  return (
    <div
      role="row"
      tabIndex={0}
      data-trace-row={row.id}
      data-pinned={pinned ? "" : undefined}
      onClick={() => onOpen(row, title)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(row, title); }}
      className={`group/row relative grid ${COLS} min-h-[54px] cursor-pointer items-center py-[8px] outline-none focus-visible:bg-[color-mix(in_srgb,var(--ink)_4%,transparent)] ${
        pinned
          ? `${lit ? "bg-[color-mix(in_srgb,var(--green)_13%,var(--bg))]" : "bg-[color-mix(in_srgb,var(--green)_7%,var(--bg))]"} before:absolute before:inset-y-[-1px] before:left-0 before:w-[2px] before:bg-[color:var(--green)] ${last ? "mb-[6px] border-b border-[color:var(--line-strong)]" : "border-b border-[color:color-mix(in_srgb,var(--green)_14%,var(--bg))]"}`
          : "border-b border-[color:var(--line-soft)] hover:bg-[color-mix(in_srgb,var(--ink)_2.5%,transparent)]"
      }`}
    >
      <span className="whitespace-nowrap pl-[18px] pr-[14px] text-[12px] tabular-nums text-[color:var(--muted)]">{whenText(row.lastMs)}</span>
      <span className="min-w-0 pr-[14px]" aria-busy={title === undefined || row.person === undefined ? "true" : undefined}>
        {/* undefined while its title is read; null once read without one */}
        {title === undefined ? (
          <span aria-hidden="true" className="my-[5px] block h-[9px] w-[52%] rounded-[3px] bg-[color-mix(in_srgb,var(--ink)_7%,transparent)]" />
        ) : (
          <span className={`block truncate text-[14px] leading-[19px] ${title ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>{title || "Untitled trace"}</span>
        )}
        <span className="flex min-w-0 items-center gap-[6px] text-[12px] leading-[17px] text-[color:var(--muted)]">
          {/* undefined while who ran it is read */}
          {row.person === undefined ? (
            <span aria-hidden="true" className="my-[4px] block h-[9px] w-[30%] rounded-[3px] bg-[color-mix(in_srgb,var(--ink)_7%,transparent)]" />
          ) : (
            <>
              <span className="truncate">{row.person || "No person"}{row.group ? `, ${row.group}` : ""}</span>
              {row.person && !row.personIsMachine ? <FilterButtons name={row.person} clause={`identity:${quote(row.person)}`} onFilter={onFilter} /> : null}
            </>
          )}
        </span>
      </span>
      <span className={`min-w-0 pr-[14px] ${SHOWN_FROM_480}`}>
        <span className="flex items-center gap-[6px] text-[13px] leading-[19px]">
          <span className="truncate">{agent}</span>
          <FilterButtons name={agent} clause={`agent:${quote(agent)}`} onFilter={onFilter} />
        </span>
        <span className="block truncate text-[12px] leading-[17px] text-[color:var(--muted)]">{row.model}</span>
      </span>
      <span className={`whitespace-nowrap pr-[14px] text-[13px] tabular-nums text-[color:var(--muted)] ${SHOWN_FROM_740}`}>{durationText(row.startMs, row.lastMs)}</span>
      <span className="whitespace-nowrap pr-[14px] text-right text-[13px] tabular-nums">{price}</span>
      <span className={`min-w-0 pr-[8px] ${SHOWN_FROM_740}`} aria-busy={row.bySev === undefined ? "true" : undefined}>
        {f ? (
          <>
            <span className="flex items-center gap-[6px] whitespace-nowrap text-[13px] leading-[19px] tabular-nums">
              <i aria-hidden="true" className="h-[7px] w-[7px] shrink-0" style={{ background: SEV_COLOR[f.sev] }} />{f.first}
            </span>
            {f.rest ? <span className="block truncate pl-[13px] text-[12px] leading-[17px] tabular-nums text-[color:var(--muted)]">{f.rest}</span> : null}
          </>
        ) : null}
      </span>
    </div>
  );
});

export default function TraceList({ rows, pinned = [], titles, costs, status, shown, onMore, lit = null, onOpen, onFilter }) {
  const pinnedIds = new Set(pinned.map((r) => r.id));
  const rest = rows.filter((r) => !pinnedIds.has(r.id));
  const more = rest.length - shown;
  const reading = status === "loading";
  return (
    <div role="table" aria-label="Traces" aria-busy={reading ? "true" : undefined} className="@container mt-[18px] flex min-h-0 flex-1 flex-col">
      <div role="row" className={`grid ${COLS} h-[28px] shrink-0 items-center border-b border-[color:var(--line)] text-[11.5px] text-[color:var(--muted-soft)]`}>
        <span role="columnheader" className="pl-[18px]">Time</span>
        <span role="columnheader">Trace</span>
        <span role="columnheader" className={SHOWN_FROM_480}>Agent</span>
        <span role="columnheader" className={SHOWN_FROM_740}>Duration</span>
        <span role="columnheader" className="pr-[14px] text-right">Cost</span>
        <span role="columnheader" className={SHOWN_FROM_740}>Findings</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {pinned.map((row, i) => (
          <Row key={row.id} row={row} title={titles.get(row.id)} cost={costs.get(row.id)} pinned last={i === pinned.length - 1} lit={lit === row.id} onOpen={onOpen} onFilter={onFilter} />
        ))}
        {rest.slice(0, shown).map((row) => (
          <Row key={row.id} row={row} title={titles.get(row.id)} cost={costs.get(row.id)} onOpen={onOpen} onFilter={onFilter} />
        ))}
        {status === "error" ? <p className="m-0 px-[18px] py-[14px] text-[13px] text-[color:var(--danger)]">The traces could not be read. Reload to try again.</p> : null}
        {status === "done" && !rows.length ? <p className="m-0 px-[18px] py-[14px] text-[13px] text-[color:var(--muted)]">No traces match this search in this range.</p> : null}
        {more > 0 ? (
          <button type="button" onClick={onMore} className="w-full px-[18px] py-[12px] text-left text-[13px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
            {NUM.format(more)} more {more === 1 ? "trace" : "traces"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
